<template>
  <div>
    <b-modal
      id="modal-delete-nicu-price"
      :no-close-on-backdrop="true"
      :title="$t('common.delete')"
      ok-only
      :ok-title="$t('common.delete')"
      @ok="deleteItem"
    >
      <b-card-text>
        <h5>{{ $t("common.delete-sure") }}</h5>
        {{ $t("common.sure-click") }}
      </b-card-text>
    </b-modal>

    <b-modal
      id="modal-edit-nicu-price"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="createMode ? $t('incubator.set_price_nicu') : $t('common.edit')"
      :ok-title="$t('common.save')"
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <AutoComplete
              v-model="EditForm.incubator_type_id"
              :label="$t('incubator.Type')"
              :placeholder="`${$t('common.select')} ${$t('incubator.Type')}`"
              :serializer="(val) => val.id"
              param-query="search"
              endpoint="incubatorTypes"
              text-title="name"
            />
          </b-col>
          <b-col md="12">
            <b-form-group :label="$t('common.price')" label-for="NICU_PRICE">
              <b-form-input
                id="NICU_PRICE"
                v-model="EditForm.price"
                type="number"
                min="0"
                :placeholder="$t('common.price')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3>{{ $t("incubator.set_price_nicu") }}</h3>
        <!-- v-if="$store.state.app.permissions.includes('store-doctorNICU')" -->
        <b-button variant="primary" @click="show(EditForm, 'create')">
          <feather-icon icon="PlusIcon" class="mr-50" />
          {{ $t("common.add") }} {{ $t("common.price") }}
        </b-button>
      </div>

      <b-row class="mb-2">
        <b-col md="4" cols="12">
          <AutoComplete
            v-model="incubatorTypeFilter"
            :label="$t('incubator.Type')"
            :placeholder="`${$t('common.select')} ${$t('incubator.Type')}`"
            param-query="search"
            :serializer="(val) => val.id"
            endpoint="incubatorTypes"
            text-title="name"
          />
        </b-col>
        <b-col md="4" cols="12">
          <b-form-group :label="$t('common.price')" label-for="priceFilter">
            <b-form-input
              id="priceFilter"
              v-model="priceFilter"
              type="number"
              :placeholder="$t('common.price')"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          cols="12"
          class="d-flex align-items-end justify-content-start mb-1"
        >
          <b-button variant="primary" @click="getDoctorNICUPrices()">
            {{ $t("common.search") }}
            <feather-icon icon="SearchIcon" class="ml-50" />
          </b-button>
        </b-col>
      </b-row>

      <b-table
        v-if="!loading"
        :items="data"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :empty-text="$t('common.no_results')"
        class="position-relative"
      >
        <template #cell(incubator_type_name)="ctx">
          <span class="text-nowrap">
            {{ ctx.item.incubator_type_name }}
          </span>
        </template>

        <template #cell(price)="ctx">
          <b-badge variant="light-success" class="font-weight-bold">
            {{ ctx.item.price }} IQD
          </b-badge>
        </template>

        <template #cell(created_at)="ctx">
          <span class="text-nowrap">
            {{ formatDate(ctx.item.created_at) }}
          </span>
        </template>

        <template #cell(actions)="ctx">
          <div class="text-nowrap">
            <b-dropdown
              variant="link"
              toggle-class="p-0"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                v-if="
                  $store.state.app.permissions.includes('update-doctorNICU')
                "
                @click="show(ctx.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t("common.edit") }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="
                  $store.state.app.permissions.includes('delete-doctorNICU')
                "
                @click="deleteModel(ctx.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{
                  $t("common.delete")
                }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <template v-if="loading">
        <div>
          <div class="d-flex justify-content-center mb-1">
            <b-spinner :label="$t('common.loading')" />
          </div>
        </div>
      </template>

      <div v-if="!loading && data.length > 0" class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
              {{ $t("common.to") }} {{ Math.min(page * take, dataCount) }}
              {{ $t("common.of") }} {{ dataCount }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
            @click="getDoctorNICUPrices()"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BModal,
  BCardText,
  BBadge,
} from "bootstrap-vue";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    AutoComplete,
    BModal,
    BCardText,
    BBadge,
  },
  props: {
    doctorId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      take: 10,
      loading: true,
      dataCount: 0,
      data: [],
      EditForm: {
        doctor_id: "",
        incubator_type_id: "",
        price: "",
        _method: "PUT",
      },
      deleteForm: {},
      createMode: false,
      params: {
        form: {},
        path: "",
      },
      tableColumns: [
        {
          key: "incubator_type_name",
          label: this.$t("incubator.Type"),
          sortable: true,
        },
        {
          key: "price",
          label: this.$t("common.price"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("common.created_at"),
          sortable: true,
        },
        { key: "actions", label: this.$t("common.actions") },
      ],
      page: 1,
      incubatorTypeFilter: null,
      priceFilter: null,
    };
  },
  watch: {
    doctorId() {
      this.getDoctorNICUPrices();
    },
  },
  mounted() {
    this.getDoctorNICUPrices();
  },
  methods: {
    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          doctor_id: this.doctorId,
          incubator_type_id: "",
          price: "",
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.EditForm.id = item.id;
        this.EditForm.doctor_id = item.doctor_id;
        this.EditForm.incubator_type_id = item.incubator_type_id;
        this.EditForm.price = item.price;
        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-edit-nicu-price");
    },

    async getDoctorNICUPrices() {
      this.loading = true;

      let url = `/doctorNICU?filter[doctor_id]=${this.doctorId}&page=${this.page}&take=${this.take}&sort=-id`;

      if (this.incubatorTypeFilter) {
        url += `&filter[incubator_type_id]=${this.incubatorTypeFilter}`;
      }
      if (this.priceFilter) {
        url += `&filter[price]=${this.priceFilter}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete-nicu-price");
    },

    deleteItem() {
      this.$store
        .dispatch("delete", `/doctorNICU/${this.deleteForm.id}`)
        .then(() => {
          this.getDoctorNICUPrices();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.delete_success"),
              icon: "CheckIcon",
              variant: "success",
              text: this.$t("common.delete_success"),
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("common.error"),
            },
          });
        });
    },

    submitItem() {
      const payload = { ...this.EditForm };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });

      if (this.createMode) {
        this.params.form = payload;
        this.params.path = "/doctorNICU";
        this.$store
          .dispatch("create", this.params)
          .then(() => {
            this.getDoctorNICUPrices();
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("common.success"),
              },
            });
          })
          .catch((error) => {
            if (error.response && error.response.status === 400) {
              Object.keys(error.response.data).forEach((key) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: error.response.data[key][0],
                  },
                });
              });
            }
          });
      } else {
        this.params.form = payload;
        this.params.path = `/doctorNICU/${this.EditForm.id}`;
        this.$store
          .dispatch("update", this.params)
          .then(() => {
            this.getDoctorNICUPrices();
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.success"),
                variant: "success",
                text: this.$t("common.success"),
              },
            });
          })
          .catch((error) => {
            if (error.response && error.response.status === 400) {
              Object.keys(error.response.data).forEach((key) => {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Validation Error",
                    variant: "danger",
                    text: error.response.data[key][0],
                  },
                });
              });
            }
          });
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    },
  },
};
</script>

<style lang="scss" scoped>
.font-weight-bold {
  font-weight: 600;
}
</style>
